import { Signal, signal } from '@angular/core'

import type { ChatHeaderDisplayMode } from '@flowx/angular-ui-toolkit'
import {
  type ChatFeedbackValue,
  type ChatMessageFeedback,
  submitChatMessageFeedback,
  theme,
} from '@flowx/core-sdk'
import { ThemeComponent, getComponentStyles } from '@flowx/core-theme'

const chatMessageFeedbackToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatMessageFeedbackFloating,
  fullscreen: ThemeComponent.ChatMessageFeedbackFullScreen,
  fill: ThemeComponent.ChatMessageFeedback,
}

const chatMessageFeedbackReasonToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatMessageFeedbackReasonFloating,
  fullscreen: ThemeComponent.ChatMessageFeedbackReasonFullScreen,
  fill: ThemeComponent.ChatMessageFeedbackReason,
}

export interface ChatFeedbackClassNames {
  modal: string
  modalHeader: string
  body: string
  textArea: string
  error: string
  button: string
}

function themedClassName(...components: ThemeComponent[]): string {
  return [
    ...components.map((component) => getComponentStyles(component, theme())).filter(Boolean),
    'flx-themed',
  ].join(' ')
}

/**
 * The feedback modal renders in an overlay outside the themed `flx-chat` host, so every slot gets
 * its theme classes explicitly. The body hosts both feedback classes — the reason modifiers are
 * descendant selectors.
 */
export function getChatFeedbackClassNames(mode: ChatHeaderDisplayMode): ChatFeedbackClassNames {
  return {
    modal: themedClassName(ThemeComponent.Modal),
    modalHeader: themedClassName(ThemeComponent.ModalHeader),
    body: themedClassName(
      chatMessageFeedbackToComponentType[mode],
      chatMessageFeedbackReasonToComponentType[mode]
    ),
    textArea: themedClassName(ThemeComponent.Textarea),
    error: themedClassName(ThemeComponent.Error),
    button: themedClassName(ThemeComponent.Button),
  }
}

export interface ChatFeedbackTarget {
  messageId: string
  feedback: ChatMessageFeedback | null
}

export interface ChatMessageFeedbackState {
  feedbackTarget: Signal<ChatFeedbackTarget | null>
  handleFeedbackChange: (
    messageId: string,
    value: ChatFeedbackValue | null,
    currentFeedback: ChatMessageFeedback | null
  ) => void
  handleFeedbackSubmit: (feedback: Omit<ChatMessageFeedback, 'value'>) => void
  handleFeedbackRemove: () => void
  handleFeedbackCancel: () => void
}

/**
 * Owns the negative-feedback form state shared by both Angular chat stacks. `onFeedbackSent` runs
 * for a thumbs up and for a submitted thumbs down. `onFeedbackUnset` runs with the removed rating
 * when a second click clears a thumbs up or the modal removes a thumbs down. Cancelling calls
 * neither.
 */
export function createChatMessageFeedback(
  onFeedbackSent?: (value: ChatFeedbackValue, reason?: string | null) => void,
  onFeedbackUnset?: (removedValue: ChatFeedbackValue) => void
): ChatMessageFeedbackState {
  const feedbackTarget = signal<ChatFeedbackTarget | null>(null)

  const handleFeedbackChange = (
    messageId: string,
    value: ChatFeedbackValue | null,
    currentFeedback: ChatMessageFeedback | null
  ): void => {
    if (value === 'NEGATIVE') {
      feedbackTarget.set({
        messageId,
        feedback: currentFeedback?.value === 'NEGATIVE' ? currentFeedback : null,
      })
      return
    }
    void submitChatMessageFeedback(messageId, value ? { value } : null)
    if (value) {
      onFeedbackSent?.(value)
    } else if (currentFeedback) {
      onFeedbackUnset?.(currentFeedback.value)
    }
  }

  const handleFeedbackSubmit = (feedback: Omit<ChatMessageFeedback, 'value'>): void => {
    const target = feedbackTarget()
    if (!target) {
      return
    }
    feedbackTarget.set(null)
    void submitChatMessageFeedback(target.messageId, { value: 'NEGATIVE', ...feedback })
    onFeedbackSent?.('NEGATIVE', feedback.reason)
  }

  const handleFeedbackRemove = (): void => {
    const target = feedbackTarget()
    if (!target) {
      return
    }
    feedbackTarget.set(null)
    void submitChatMessageFeedback(target.messageId, null)
    if (target.feedback) {
      onFeedbackUnset?.(target.feedback.value)
    }
  }

  const handleFeedbackCancel = (): void => feedbackTarget.set(null)

  return {
    feedbackTarget: feedbackTarget.asReadonly(),
    handleFeedbackChange,
    handleFeedbackSubmit,
    handleFeedbackRemove,
    handleFeedbackCancel,
  }
}
